import { useState, useEffect, useRef } from 'react';
import { X, Loader } from 'lucide-react';
import { createContact } from '../services/api';

interface AddContactModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddContactModal({ open, onClose, onSuccess }: AddContactModalProps) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setForm({ first_name: '', last_name: '', email: '', phone: '', company: '', title: '' });
      setError('');
      // Auto-focus after render
      setTimeout(() => firstFieldRef.current?.focus(), 100);
    }
  }, [open]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.company.trim()) payload.organization_name = form.company.trim();
      if (form.title.trim()) payload.title = form.title.trim();

      await createContact(payload);
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create contact');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-primary)',
    color: 'var(--text-primary)',
  };

  const fields: { key: keyof typeof form; label: string; type: string; placeholder: string }[] = [
    { key: 'first_name', label: 'First Name', type: 'text', placeholder: 'Jane' },
    { key: 'last_name', label: 'Last Name', type: 'text', placeholder: 'Smith' },
    { key: 'email', label: 'Email', type: 'email', placeholder: 'jane@company.com' },
    { key: 'phone', label: 'Phone', type: 'tel', placeholder: '+1 (555) 123-4567' },
    { key: 'company', label: 'Company', type: 'text', placeholder: 'Acme Corp' },
    { key: 'title', label: 'Title', type: 'text', placeholder: 'VP of Sales' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.75)' }}
        onClick={onClose}
      />

      {/* Modal — bottom sheet on mobile, centered on desktop */}
      <div
        className="relative w-full md:w-[480px] md:max-h-[90vh] max-h-[85vh] overflow-y-auto rounded-t-2xl md:rounded-2xl p-6 slide-up"
        style={{
          background: '#141414',
          border: '1px solid var(--border-primary)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-lg" style={{ color: 'var(--text-primary)' }}>
            Add Contact
          </h3>
          <button
            onClick={onClose}
            className="min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg cursor-pointer"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Drag handle on mobile */}
        <div className="md:hidden flex justify-center -mt-4 mb-4">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border-subtle)' }} />
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--error)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((field, i) => (
            <div key={field.key}>
              <label className="block text-xs mb-1.5 font-body" style={{ color: 'var(--text-secondary)' }}>
                {field.label}{field.key === 'first_name' && ' *'}
              </label>
              <input
                ref={i === 0 ? firstFieldRef : undefined}
                type={field.type}
                value={form[field.key]}
                onChange={handleChange(field.key)}
                placeholder={field.placeholder}
                className="w-full px-4 min-h-[48px] rounded-lg text-sm outline-none transition-all duration-200"
                style={inputStyle}
                onFocus={(e) => e.currentTarget.style.borderColor = 'var(--netrun-green)'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-primary)'}
              />
            </div>
          ))}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-[48px] rounded-lg text-sm font-body cursor-pointer transition-all"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 min-h-[48px] rounded-lg text-sm font-body cursor-pointer transition-all flex items-center justify-center gap-2"
              style={{
                background: 'var(--netrun-green)',
                border: 'none',
                color: 'var(--netrun-black)',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting && <Loader size={16} className="animate-spin" />}
              {submitting ? 'Saving...' : 'Save Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
